import * as React from 'react';
import { Search, X } from 'lucide-react';
import { AgnesInput } from '@agnes/ui';

interface SearchInputProps {
  value: string;
  onValueChange: (value: string) => void;
  onSearch: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  dataQa?: string;
}

export function SearchInput({
  value,
  onValueChange,
  onSearch,
  placeholder,
  debounceMs = 400,
  className,
  dataQa,
}: SearchInputProps) {
  const isFirstRender = React.useRef(true);
  const skipNextDebounce = React.useRef(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (skipNextDebounce.current) {
      skipNextDebounce.current = false;
      return;
    }
    timerRef.current = setTimeout(() => onSearch(value), debounceMs);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleClear = () => {
    clearTimeout(timerRef.current);
    skipNextDebounce.current = true;
    onValueChange('');
    onSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      clearTimeout(timerRef.current);
      onSearch(value);
    }
  };

  return (
    <div className={`relative w-full${className ? ` ${className}` : ''}`}>
      <AgnesInput
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        startIcon={<Search className="h-4 w-4" />}
        dataQa={dataQa}
        style={value ? { paddingRight: '2.25rem' } : undefined}
      />
      {value && (
        <button
          type="button"
          aria-label="Limpar busca"
          data-qa={dataQa ? `${dataQa}-limpar` : undefined}
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-agnes-text-muted hover:text-agnes-text-primary transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
